import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Tag, Spin, message, Modal } from 'antd';
import { ClockCircleOutlined, EyeOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { QRCode } from 'antd';
import { articleAPI, paymentsAPI } from '../services/api';
import './ClassroomDetail.css';

function ClassroomDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrVisible, setQrVisible] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [currentOrderNo, setCurrentOrderNo] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);

  useEffect(() => {
    loadArticle();
    return () => {
      // 组件卸载时清除轮询
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [id]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const res = await articleAPI.getArticleById(id);
      setArticle(res.data);

      // 如果是付费文章，检查用户访问权限
      if (res.data.is_premium === 1) {
        checkAccess();
      } else {
        setHasAccess(true);
        setAccessChecked(true);
      }
    } catch (error) {
      message.error('加载文章失败');
      navigate('/classroom');
    } finally {
      setLoading(false);
    }
  };

  const checkAccess = async () => {
    try {
      const res = await paymentsAPI.checkArticleAccess(id);
      setHasAccess(res.data.has_access);
      setAccessChecked(true);

      if (res.data.has_access) {
        const articleRes = await articleAPI.getArticleById(id);
        setArticle(articleRes.data);
      }
    } catch (error) {
      setHasAccess(false);
      setAccessChecked(true);
    }
  };

  const handleShowPayment = async () => {
    setPaymentLoading(true);
    setQrVisible(true);
    try {
      console.log('[支付调试] 开始创建支付，文章ID:', id);
      const res = await paymentsAPI.createArticlePayment({ article_id: id });
      console.log('[支付调试] 支付创建成功:', res);
      setPaymentUrl(res.data.payment_url);
      setCurrentOrderNo(res.data.order_no);

      // 开启轮询检测支付状态
      startPolling(res.data.order_no);
    } catch (error) {
      console.error('[支付调试] 支付创建失败:', error);
      message.error(error.message || '获取支付链接失败');
      setPaymentUrl('');
      setQrVisible(false);
    } finally {
      setPaymentLoading(false);
    }
  };

  const startPolling = (orderNo) => {
    // 清除旧的轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    console.log('[轮询调试] 开始轮询订单:', orderNo);
    let pollCount = 0;
    const maxPolls = 100; // 最多轮询100次（5分钟）

    const interval = setInterval(async () => {
      pollCount++;
      console.log(`[轮询调试] 第${pollCount}次轮询，订单号:`, orderNo);

      try {
        const res = await paymentsAPI.getOrderStatus(orderNo);
        console.log(`[轮询调试] 第${pollCount}次轮询结果:`, res);

        if (res.data.is_paid) {
          // 支付成功
          console.log('[轮询调试] 检测到支付成功！');
          clearInterval(interval);
          setPollingInterval(null);
          message.success('支付成功！');
          setQrVisible(false);

          // 重新加载文章，解锁完整内容
          setHasAccess(true);
          const articleRes = await articleAPI.getArticleById(id);
          setArticle(articleRes.data);
        } else {
          console.log(`[轮询调试] 第${pollCount}次检测，订单状态: ${res.data.status}，未支付`);
        }
      } catch (error) {
        console.error('[轮询调试] 轮询订单状态失败:', error);
      }

      // 达到最大轮询次数，停止轮询
      if (pollCount >= maxPolls) {
        console.log('[轮询调试] 达到最大轮询次数，停止轮询');
        clearInterval(interval);
        setPollingInterval(null);
        message.info('支付超时，请刷新页面查看');
      }
    }, 3000); // 每3秒轮询一次

    setPollingInterval(interval);
  };

  const handleClosePayment = () => {
    setQrVisible(false);
    setPaymentUrl('');

    // 停止轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!article) {
    return null;
  }

  // 判断是否显示完整内容
  const showFullContent = !article.is_premium || (article.is_premium === 1 && hasAccess);

  return (
    <div className="classroom-detail-container">
      <div className="detail-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/classroom')}
        >
          返回列表
        </Button>
      </div>

      <Card className="article-card">
        {article.cover_image && (
          <div className="article-cover-image">
            <img
              src={article.cover_image}
              alt={article.title}
              onError={(e) => { e.target.src = '/images/default-article.svg'; }}
            />
          </div>
        )}

        <div className="article-header">
          <h1 className="article-title">{article.title}</h1>

          <div className="article-info">
            <Tag color="blue">{article.category_name}</Tag>
            {article.is_premium === 1 && (
              <Tag color="gold">付费文章 ¥{article.price}</Tag>
            )}
            {hasAccess && article.is_premium === 1 && (
              <Tag color="green">已购买</Tag>
            )}
            <span className="article-meta-item">
              <ClockCircleOutlined /> {new Date(article.created_at).toLocaleDateString()}
            </span>
            <span className="article-meta-item">
              <EyeOutlined /> {article.views || 0} 次浏览
            </span>
          </div>
        </div>

        {article.summary && (
          <div className="article-summary">
            <h3>摘要</h3>
            <p>{article.summary}</p>
          </div>
        )}

        <div className="article-content">
          {showFullContent ? (
            // 显示完整内容
            <div className="content-full" dangerouslySetInnerHTML={{ __html: article.content?.replace(/\n/g, '<br/>') }} />
          ) : (
            // 显示预览和支付提示
            <>
              {article.content_preview && (
                <div className="content-preview">
                  <div dangerouslySetInnerHTML={{ __html: article.content_preview?.replace(/\n/g, '<br/>') }} />
                </div>
              )}
              {accessChecked && (
                <div className="payment-prompt">
                  <div className="payment-box">
                    <h3>查看完整内容</h3>
                    <p>本文为付费内容，需要支付 <strong>¥{article.price}</strong> 查看完整版本</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleShowPayment}
                      loading={paymentLoading}
                    >
                      立即支付查看
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* 付费二维码弹窗 */}
      <Modal
        title="扫码支付查看完整内容"
        open={qrVisible}
        onCancel={handleClosePayment}
        footer={null}
        centered
        width={400}
      >
        <div className="payment-modal">
          <div className="qr-code-container">
            {paymentLoading ? (
              <Spin tip="生成支付链接中..." />
            ) : paymentUrl ? (
              <>
                <QRCode value={paymentUrl} size={240} />
                <p style={{ marginTop: 16, textAlign: 'center', color: '#1890ff' }}>
                  正在等待支付...
                </p>
              </>
            ) : (
              <p>未获取到支付链接，请稍后再试。</p>
            )}
          </div>
          <div className="payment-info">
            <h3>{article.title}</h3>
            <div className="payment-price">
              <span className="price-label">支付金额：</span>
              <span className="price-value">¥{article.price}</span>
            </div>
            <p className="payment-tip">请使用微信或支付宝扫码支付</p>
            <p className="payment-tip">支付成功后将自动解锁完整内容</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ClassroomDetail;
